import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  convertTZ,
  getActiveTime,
  getEventId,
  isEndOfDay,
  mergeInactiveEvents,
  roundTo15Min,
  splitEventAtMidnight,
} from "common/Calendars/utils.ts";
import { hexToRGB } from "@shared/utils/misc.ts";
import { useTheme } from "styled-components";
import { EventPropGetter, SlotInfo } from "react-big-calendar";
import { OperationHoursEvent } from "@shared/types";
import { usePrevious } from "@shared/utils/hooks/misc.ts";
import { MAX_DAY_END_TIME } from "common/Calendars/constants.ts";
import { useBottomNavigator } from "utils/hooks/misc.tsx";

interface useCalendarProps {
  myEvents: OperationHoursEvent[];
  setMyEvents?: Dispatch<SetStateAction<OperationHoursEvent[] | undefined>>;
  timezone?: string;
  viewOnly?: boolean;
}

const useCalendar = ({
  myEvents,
  setMyEvents,
  timezone,
  viewOnly,
}: useCalendarProps) => {
  const theme = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const prevTimezone = usePrevious(timezone) || "";

  const [popoverTarget, setPopoverTarget] = useState<{
    right: number;
    top: number;
  } | null>(null);
  const [showPopover, setShowPopover] = useState(false);
  const [selectedEvent, setSelectedEvent] =
    useState<OperationHoursEvent | null>(null);

  const events = useMemo(() => {
    const activeEvents = getActiveTime(myEvents);
    return [...myEvents, ...activeEvents];
  }, [myEvents, timezone]);

  useEffect(() => {
    if (timezone) {
      setMyEvents?.((prev) => {
        const updatedEvents: OperationHoursEvent[] = [];
        prev?.forEach((event) => {
          if (!event.start || !event.end) return event;
          const startTz = convertTZ(
            event.start,
            prevTimezone,
            timezone,
            "start"
          );
          const endTz = convertTZ(event.end, prevTimezone, timezone, "end");
          const splitEvents = splitEventAtMidnight(event, startTz, endTz);
          updatedEvents.push(...splitEvents);
        });
        return mergeInactiveEvents(updatedEvents);
      });
    }
  }, [timezone]);

  useEffect(() => {
    if (!selectedEvent?.id) return;
    const element = document.getElementById(selectedEvent.id);
    if (element) {
      const rect = element.getBoundingClientRect();
      setPopoverTarget(rect);
      setShowPopover(true);
    }
  }, [selectedEvent]);

  const handleSelectSlot = useCallback(
    ({ start, end }: SlotInfo) => {
      if (showPopover) return;
      setMyEvents?.((prev) => {
        const editedEnd = new Date(end);
        if (isEndOfDay(end)) {
          editedEnd.setHours(...MAX_DAY_END_TIME);
        }
        const newEvent = {
          start,
          end: editedEnd,
          id: getEventId(start, editedEnd),
          isActive: false,
        };
        setSelectedEvent(newEvent);
        const updatedEvents = [...(prev || []), newEvent];
        return mergeInactiveEvents(updatedEvents);
      });
    },
    [setMyEvents, showPopover, timezone]
  );

  const moveEvent = ({ event, start, end }) => {
    setMyEvents?.((prev) => {
      const filtered = prev?.filter((e) => e.id !== event.id);

      const roundedStart = roundTo15Min(start, "start");
      const roundedEnd = roundTo15Min(end, "end");
      if (roundedStart.getTime() === roundedEnd.getTime()) {
        return filtered;
      }

      const splitEvents = splitEventAtMidnight(event, roundedStart, roundedEnd);
      const updatedEvents = [...(filtered || []), ...splitEvents];
      return mergeInactiveEvents(updatedEvents);
    });
  };

  const timeGutterFormat = (date, culture, localizer) =>
    localizer.format(date, "hA", culture);

  const eventPropGetter: EventPropGetter<OperationHoursEvent> = (event) => {
    return {
      style: event.isActive
        ? {
            backgroundColor: "transparent",
            border: "none",
            boxShadow: "none",
            pointerEvents: "none",
          }
        : {
            background: hexToRGB(
              theme.colors.secondaryGrey,
              theme.isDarkMode ? 0.2 : 0.04
            ),
            borderLeft: `4px solid ${theme.colors.secondaryGrey}`,
            color: theme.colors.mediumGrey,
            pointerEvents: "auto",
          },
    };
  };

  const allowDragAndResize = (event: OperationHoursEvent) =>
    !event.isActive && !viewOnly && !isBottomNavigator;

  const handleDeleteEvent = () => {
    setShowPopover(false);
    setMyEvents?.((prev) => prev?.filter((e) => e.id !== selectedEvent?.id));
    setSelectedEvent(null);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        !viewOnly &&
        (event.key === "Delete" || event.key === "Backspace") &&
        selectedEvent
      ) {
        handleDeleteEvent();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedEvent, viewOnly]);

  return {
    events,
    handleSelectSlot,
    moveEvent,
    timeGutterFormat,
    eventPropGetter,
    allowDragAndResize,
    popoverTarget,
    showPopover,
    setShowPopover,
    selectedEvent,
    setSelectedEvent,
    handleDeleteEvent,
  };
};

export default useCalendar;
